import React, { useEffect, useState, useCallback } from 'react';
import { listRules, createRule, updateRule, deleteRule, listBuiltinRules,
         downloadMyData, deleteMyData } from '../services/api';

const EMPTY_RULE = {
  name: '',
  match_field: 'description',
  match_type: 'contains',
  match_value: '',
  amount_min: '',
  amount_max: '',
  action_type: 'code',
  account_code: '',
  contact_name: '',
  notes: '',
  enabled: true
};

export default function RulesPanel({ tenantId }) {
  const [rules, setRules] = useState([]);
  const [builtin, setBuiltin] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [draft, setDraft] = useState(EMPTY_RULE);
  const [saveError, setSaveError] = useState(null);
  const [showBuiltin, setShowBuiltin] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    Promise.all([
      listRules(tenantId).catch(() => []),
      listBuiltinRules().catch(() => [])
    ]).then(([userRules, defaults]) => {
      setRules(userRules || []);
      setBuiltin(defaults || []);
    }).finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => { refresh(); }, [refresh]);

  const startNew = () => {
    setDraft({ ...EMPTY_RULE });
    setEditing('new');
    setSaveError(null);
  };

  const startEdit = (rule) => {
    setDraft({
      ...rule,
      amount_min: rule.amount_min ?? '',
      amount_max: rule.amount_max ?? '',
      enabled: !!rule.enabled
    });
    setEditing(rule.id);
    setSaveError(null);
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft(EMPTY_RULE);
    setSaveError(null);
  };

  const save = async () => {
    setSaveError(null);
    try {
      const payload = {
        ...draft,
        amount_min: draft.amount_min === '' ? null : Number(draft.amount_min),
        amount_max: draft.amount_max === '' ? null : Number(draft.amount_max),
        enabled: draft.enabled ? 1 : 0
      };
      if (editing === 'new') await createRule(payload);
      else await updateRule(editing, payload);
      cancelEdit();
      refresh();
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message);
    }
  };

  const toggleEnabled = async (rule) => {
    await updateRule(rule.id, { enabled: rule.enabled ? 0 : 1 });
    refresh();
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name || rule.match_value}"?`)) return;
    await deleteRule(rule.id);
    refresh();
  };

  if (loading) return <div className="rules-panel"><div className="spinner" /></div>;

  return (
    <div className="rules-panel">
      <BuiltinRules rules={builtin} expanded={showBuiltin} onToggle={() => setShowBuiltin(v => !v)} />

      <div className="rules-header">
        <div>
          <h2 className="rules-title">Your coding rules</h2>
          <p className="rules-sub">
            Your rules run first (pass 0), before the built-in matching above.
            Use them for recurring patterns — Stripe payouts, HMRC payments,
            bank fees, subscriptions — so they're classified automatically
            instead of being flagged as unmatched.
          </p>
        </div>
        {editing === null && (
          <button className="btn-primary" onClick={startNew}>+ New rule</button>
        )}
      </div>

      {editing !== null && (
        <RuleEditor
          draft={draft}
          setDraft={setDraft}
          onSave={save}
          onCancel={cancelEdit}
          error={saveError}
          isNew={editing === 'new'}
        />
      )}

      {rules.length === 0 && editing === null && (
        <div className="empty-tab">
          No rules yet. Click "New rule" to define one, or click "Save as rule" on a
          coded transaction in the reconcile view.
        </div>
      )}

      <div className="rules-list">
        {rules.map(rule => (
          <RuleRow
            key={rule.id}
            rule={rule}
            onEdit={() => startEdit(rule)}
            onToggle={() => toggleEnabled(rule)}
            onDelete={() => remove(rule)}
            isEditing={editing === rule.id}
          />
        ))}
      </div>

      <YourDataPanel onAfterDelete={refresh} />
    </div>
  );
}

function YourDataPanel({ onAfterDelete }) {
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const handleExport = async () => {
    setBusy(true); setFeedback(null);
    try {
      await downloadMyData();
      setFeedback('Export downloaded.');
    } catch (err) {
      setFeedback(`Export failed: ${err.message}`);
    } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      'Permanently delete every reconciliation job, posted-payment record, and coding rule attached to your account?\n\n' +
      'This cannot be undone. Your Xero data is unaffected.'
    );
    if (!confirmed) return;
    setBusy(true); setFeedback(null);
    try {
      const result = await deleteMyData();
      setFeedback(
        `Deleted ${result.deleted.jobs} jobs, ${result.deleted.posted_payments} payment records, ${result.deleted.coding_rules} rules.`
      );
      if (onAfterDelete) onAfterDelete();
    } catch (err) {
      setFeedback(`Delete failed: ${err.message}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="your-data-panel">
      <h3 className="your-data-title">Your data</h3>
      <p className="rules-sub">
        Under UK GDPR you can download every row this app stores about you,
        or have it all permanently deleted. Posted-payment records are retained
        for 7 years by default for accounting-law compliance — deletion below
        overrides that.
      </p>
      <div className="your-data-actions">
        <button className="btn-secondary" onClick={handleExport} disabled={busy}>
          Download my data (JSON)
        </button>
        <button className="btn-ghost danger" onClick={handleDelete} disabled={busy}>
          Delete all my data
        </button>
      </div>
      {feedback && <p className="your-data-feedback">{feedback}</p>}
    </div>
  );
}

function BuiltinRules({ rules, expanded, onToggle }) {
  if (!rules || !rules.length) return null;
  const byPass = rules.reduce((acc, r) => {
    (acc[r.pass] = acc[r.pass] || []).push(r);
    return acc;
  }, {});
  const passLabels = {
    0: 'Pass 0 — Your coding rules',
    1: 'Pass 1 — Deterministic matching',
    2: 'Pass 2 — AI fallback'
  };

  return (
    <div className="builtin-rules">
      <button className="builtin-toggle" onClick={onToggle} type="button">
        <span className="builtin-caret">{expanded ? '▾' : '▸'}</span>
        <span>Built-in matching rules</span>
        <span className="builtin-badge">read-only · {rules.length}</span>
      </button>

      {expanded && (
        <>
          <p className="rules-sub builtin-intro">
            These describe the matching strategies the app uses out of the box.
            They are baked into the code (services/matcherService.js,
            services/claudeService.js) and cannot be edited here — surfaced
            for transparency and audit. If the matcher behaviour ever changes,
            this list is updated to match.
          </p>

          {Object.keys(byPass).sort().map(pass => (
            <div key={pass} className="builtin-pass">
              <h4 className="builtin-pass-title">{passLabels[pass] || `Pass ${pass}`}</h4>
              <div className="builtin-list">
                {byPass[pass].sort((a, b) => a.order - b.order).map(r => (
                  <div key={r.id} className="builtin-row">
                    <div className="builtin-row-head">
                      <span className="builtin-row-name">{r.name}</span>
                      {r.confidence !== null && r.confidence !== undefined && (
                        <span className="builtin-row-conf">conf {r.confidence}</span>
                      )}
                      <span className={`builtin-source builtin-source-${r.source}`}>{r.source}</span>
                    </div>
                    <div className="builtin-row-desc">{r.description}</div>
                    <div className="builtin-row-meta">
                      <strong>When:</strong> {r.when}
                    </div>
                    <div className="builtin-row-meta">
                      <strong>Action:</strong> {r.action}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function RuleRow({ rule, onEdit, onToggle, onDelete, isEditing }) {
  if (isEditing) return null; // The editor is shown above the list when editing
  const fieldLabel = rule.match_field === 'both' ? 'description or reference' : rule.match_field;
  const actionLabel = rule.action_type === 'code'
    ? `code → ${[rule.contact_name, rule.account_code].filter(Boolean).join(' / ') || 'configured'}`
    : rule.action_type === 'ignore' ? 'ignore (no posting)' : 'flag for review';

  return (
    <div className={`rule-row ${rule.enabled ? '' : 'disabled'}`}>
      <div className="rule-row-main">
        <div className="rule-name">{rule.name || `(unnamed rule)`}</div>
        <div className="rule-summary">
          When {fieldLabel} {rule.match_type} <code>{rule.match_value}</code> → {actionLabel}
        </div>
        {(rule.amount_min !== null || rule.amount_max !== null) && (
          <div className="rule-meta">
            Amount: {rule.amount_min ?? '—'} to {rule.amount_max ?? '—'}
          </div>
        )}
        <div className="rule-meta">
          Hit {rule.hit_count} {rule.hit_count === 1 ? 'time' : 'times'}
          {rule.last_used && ` · last used ${new Date(rule.last_used).toLocaleDateString('en-GB')}`}
        </div>
      </div>
      <div className="rule-row-actions">
        <label className="rule-toggle">
          <input type="checkbox" checked={!!rule.enabled} onChange={onToggle} />
          <span>{rule.enabled ? 'Enabled' : 'Disabled'}</span>
        </label>
        <button className="btn-ghost" onClick={onEdit}>Edit</button>
        <button className="btn-ghost danger" onClick={onDelete}>Delete</button>
      </div>
    </div>
  );
}

function RuleEditor({ draft, setDraft, onSave, onCancel, error, isNew }) {
  const set = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });
  const setBool = (k) => (e) => setDraft({ ...draft, [k]: e.target.checked });

  return (
    <div className="rule-editor">
      <h3>{isNew ? 'New rule' : 'Edit rule'}</h3>

      <div className="form-row">
        <label>Name (optional)</label>
        <input type="text" value={draft.name} onChange={set('name')}
               placeholder="e.g. Stripe payouts" />
      </div>

      <div className="form-row">
        <label>When</label>
        <div className="form-inline">
          <select value={draft.match_field} onChange={set('match_field')}>
            <option value="description">Description</option>
            <option value="reference">Reference</option>
            <option value="both">Description or reference</option>
          </select>
          <select value={draft.match_type} onChange={set('match_type')}>
            <option value="contains">contains</option>
            <option value="exact">equals</option>
            <option value="regex">matches regex</option>
          </select>
          <input type="text" value={draft.match_value} onChange={set('match_value')}
                 placeholder={draft.match_type === 'regex' ? '^STRIPE PAYOUT' : 'STRIPE PAYOUT'}
                 className="grow" />
        </div>
      </div>

      <div className="form-row">
        <label>Amount range (optional)</label>
        <div className="form-inline">
          <input type="number" step="0.01" value={draft.amount_min}
                 onChange={set('amount_min')} placeholder="min" />
          <span>to</span>
          <input type="number" step="0.01" value={draft.amount_max}
                 onChange={set('amount_max')} placeholder="max" />
        </div>
      </div>

      <div className="form-row">
        <label>Action</label>
        <select value={draft.action_type} onChange={set('action_type')}>
          <option value="code">Code (assign contact / account)</option>
          <option value="ignore">Ignore (no posting needed)</option>
          <option value="flag">Flag for manual review</option>
        </select>
      </div>

      {draft.action_type === 'code' && (
        <>
          <div className="form-row">
            <label>Contact name</label>
            <input type="text" value={draft.contact_name} onChange={set('contact_name')}
                   placeholder="Stripe" />
          </div>
          <div className="form-row">
            <label>Account code</label>
            <input type="text" value={draft.account_code} onChange={set('account_code')}
                   placeholder="200" />
          </div>
        </>
      )}

      <div className="form-row">
        <label>Notes (optional)</label>
        <textarea value={draft.notes} onChange={set('notes')} rows={2} />
      </div>

      <div className="form-row">
        <label className="rule-toggle">
          <input type="checkbox" checked={!!draft.enabled} onChange={setBool('enabled')} />
          <span>Enabled</span>
        </label>
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="form-actions">
        <button className="btn-ghost" onClick={onCancel}>Cancel</button>
        <button className="btn-primary" onClick={onSave}
                disabled={!draft.match_value}>
          {isNew ? 'Create rule' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
